import { useMutation } from '@apollo/client';
import { Request } from 'lib/generated/graphql';
import { UPDATE_REQUEST } from 'lib/mutations';
import {
  ADMIN_FIND_REQUESTS,
  FIND_HUDDLES,
  FIND_REQUESTS_QUERY,
  GET_HUDDLE_QUERY,
  GET_REQUEST_QUERY,
} from 'lib/queries';
import { FunctionComponent, useRef } from 'react';
import FormBrief from 'src/screens/company/components/CreateProject/FormBrief';
import FormBudget from 'src/screens/company/components/CreateProject/FormBudget';
import FormGoals from 'src/screens/company/components/CreateProject/FormGoals';
import ProjectForm from 'src/screens/my-projects/components/CreateLegacyRequest/ProjectForm';
import { Button } from 'src/shared-components';
import Title from 'src/shared-components/Modal/Title';

interface UpdateContainerProps {
  section: string;
  request: Request;
  onProjectUpdated: () => void;
  setIsLoading?: (isLoading: boolean) => void;
}

const titles: any = {
  goals: 'Tell us what you’re looking for. 🔎',
  request: 'Add some color. 🎨',
};
const subTitles: any = {
  goals: 'Add the outcomes or things you’re hoping to get help with.',
  budget: 'Share with the community what you’re thinking  time and budget-wise.',
  brief: 'Add a brief and any additional details about your project and goals.',
};

const UpdateProject: FunctionComponent<UpdateContainerProps> = ({
  section,
  request,
  onProjectUpdated,
  setIsLoading,
}: UpdateContainerProps) => {
  const childForms = useRef<any>();
  const [updateRequestMutation] = useMutation(UPDATE_REQUEST, {
    refetchQueries: [GET_REQUEST_QUERY, GET_HUDDLE_QUERY, FIND_HUDDLES, FIND_REQUESTS_QUERY, ADMIN_FIND_REQUESTS],
  });

  const handleFormSubmitted = async (values: any) => {
    try {
      if (setIsLoading) {
        setIsLoading(true);
      }
      await updateRequestMutation({
        variables: {
          input: values,
          where: {
            id: request.id,
          },
        },
      });
      onProjectUpdated();
      if (setIsLoading) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="sm:max-w-[720px]">
      <Title
        title={titles[section] || 'Update Huddle'}
        subtitle={subTitles[section]}
        center={false}
        subtitleClass="text-left w-11/12"
      />
      {section === 'goals' && (
        <div className="max-h-80 overflow-scroll">
          <FormGoals
            ref={childForms}
            initialValues={request?.goals?.map(goal => goal.id)}
            onFormSubmitted={values => handleFormSubmitted({ goals: { set: values } })}
          />
        </div>
      )}
      {section === 'brief' && (
        <FormBrief
          ref={childForms}
          initialValues={{ brief: request?.brief }}
          onFormSubmitted={handleFormSubmitted}
          update
        />
      )}
      {section === 'budget' && (
        <FormBudget
          ref={childForms}
          initialValues={{
            preferredStartDate: request?.preferredStartDate,
            preferredDuration: request?.preferredDuration,
            cashBudget: request?.cashBudget,
            equityOrCrypto: request?.equityOrCrypto,
          }}
          onFormSubmitted={handleFormSubmitted}
          update
        />
      )}
      {section === 'request' && (
        <div className="w-[720px]">
          <ProjectForm
            initialValues={{
              preferredStartDate: request?.preferredStartDate,
              preferredDuration: request?.preferredDuration,
              cashBudget: request?.cashBudget,
              equityOrCrypto: request?.equityOrCrypto,
              brief: request?.brief,
            }}
            ref={childForms}
            onFormSubmitted={handleFormSubmitted}
            update
          />
        </div>
      )}
      <Button
        className="mt-5 bg-[#151515]"
        size="xl"
        roundness="md"
        color="black"
        fullWidth
        onClick={() => childForms.current?.submitForm()}
      >
        Save
      </Button>
    </div>
  );
};

export default UpdateProject;
